import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import PostSaveRepairModal from '../components/repairs/PostSaveRepairModal';
import {
  Calendar,
  Save,
  User,
  Phone,
  Mail,
  MapPin,
  AlertTriangle,
  Settings,
  Lock,
  DollarSign,
  Wrench,
  Upload,
  X,
  Package,
  PhoneOff,
} from 'lucide-react';
import { BrandSelector, ModelSelector, useBrandsModels } from '../components/BrandModelSelector';
import { RepFormHeader, RepFormSection } from '../features/reparations/reparationsUi';
import { PARTS_OPTIONS } from '../features/reparations/reparationsParts';
import RepairStockSearch from '../features/reparations/RepairStockSearch';

const ReparationFormPage = () => {
  const [showQrModal, setShowQrModal] = React.useState(false);
  const [savedRef, setSavedRef] = React.useState(null);
  const { api, user } = useAuth();
  const isCarlos = (user?.username || '').toLowerCase() === 'carlos';
  const { t, tError, formatCurrency } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { ref } = useParams();
  const isEditing = !!ref;

  // Commande pre-fill data
  const fromCommande = location.state?.fromCommande || false;
  const commandeId = location.state?.commandeId || '';
  const prefillData = location.state?.prefill || null;

  const [loading, setLoading] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [stockSearch, setStockSearch] = useState('');
  const [showStockResults, setShowStockResults] = useState(false);
  const [selectedStockParts, setSelectedStockParts] = useState([]);
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [newPhotos, setNewPhotos] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [brand, setBrand] = useState('');
  const { brands: allBrands, modelsMap: brandModelsMap, seriesMap: brandSeriesMap } = useBrandsModels(api);

  // C12.3 (2026-02) — magasin de prise en charge. Obligatoire en création.
  // Pré-rempli avec la session courante. Modifiable par raymond/samily qui
  // peuvent travailler sur n'importe quel magasin.
  // 2026-05 fix — read the canonical `pos_session_magasin_id` key written by
  // MagasinSessionPicker / PermissionsContext (legacy `pos_current_magasin_id`
  // is also checked as a soft fallback for old sessions).
  const [magasinId, setMagasinId] = useState(() => {
    try {
      return (
        localStorage.getItem('pos_session_magasin_id')
        || localStorage.getItem('pos_current_magasin_id')
        || ''
      ).toUpperCase();
    } catch (_) { return ''; }
  });
  const [magasinsList, setMagasinsList] = useState([]);
  const [forceBackdate, setForceBackdate] = useState(false);
  const [forceDepotDate, setForceDepotDate] = useState('');

  useEffect(() => {
    api.get('/magasins').then(r => {
      const raw = Array.isArray(r.data) ? r.data : (r.data?.magasins || []);
      const list = raw.filter(m => m.is_active !== false);
      setMagasinsList(list);
      if (!magasinId && list.length === 1) setMagasinId(list[0].code);
    }).catch(() => setMagasinsList([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);
  
  const [formData, setFormData] = useState({
    client_name: '',
    client_phone: '',
    client_email: '',
    client_address: '',
    model: '',
    problem: '',
    technician_name: '',
    imei_serial: '',
    device_lock: '',
    notes: '',
    price_repair: 0,
    price_diagnostic: 0,
    parts_check: {},
    selected_parts: []
  });

  const partsOptions = PARTS_OPTIONS;

  const inputClass = 'w-full rounded-lg bg-black/30 border border-white/10 text-white px-3 py-2.5 text-sm focus:outline-none focus:border-red-500/40 min-h-[44px]';
  const labelClass = 'block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5';

  useEffect(() => {
    fetchTechnicians();
    if (isEditing) {
      fetchDevice();
    } else if (fromCommande && prefillData) {
      // Pre-fill from commande conversion
      if (prefillData.magasin_id) setMagasinId(String(prefillData.magasin_id).toUpperCase());
      setFormData(prev => ({
        ...prev,
        client_name: prefillData.client_name || '',
        client_phone: prefillData.client_phone || '',
        client_email: prefillData.client_email || '',
        client_address: prefillData.client_address || '',
        problem: prefillData.problem || '',
        price_repair: prefillData.price_repair || 0,
      }));
    }
  }, [ref]);

  const fetchTechnicians = async () => {
    try {
      const response = await api.get('/users');
      setTechnicians(response.data.map(u => u.username));
    } catch (error) {
      console.error('Error fetching technicians:', error);
    }
  };

  const fetchDevice = async () => {
    try {
      const response = await api.get(`/devices/${ref}`);
      setFormData(response.data);
      setExistingPhotos(response.data.photos || []);
      // Extract brand from model name
      const model = response.data.model || '';
      const matchedBrand = (allBrands || []).find(b => model.toLowerCase().startsWith(b.toLowerCase()));
      if (matchedBrand) {
        setBrand(matchedBrand);
        setFormData(prev => ({ ...prev, model: model.slice(matchedBrand.length).trim() }));
      }
    } catch (error) {
      alert(t('repairForm.deviceNotFound'));
      navigate('/reparations');
    }
  };

  const searchClients = async (query) => {
    if (query.length < 2) {
      setClientSuggestions([]);
      return;
    }
    try {
      const response = await api.get(`/clients/autocomplete/${encodeURIComponent(query)}`);
      setClientSuggestions(response.data);
    } catch (error) {
      console.error('Error searching clients:', error);
    }
  };

  const searchStock = async (query) => {
    if (query.length < 2) {
      setStockItems([]);
      return;
    }
    try {
      const response = await api.get(`/stock/search/${encodeURIComponent(query)}`);
      setStockItems(response.data);
      setShowStockResults(true);
    } catch (error) {
      console.error('Error searching stock:', error);
    }
  };

  const selectClient = (client) => {
    setFormData({
      ...formData,
      client_name: client.nom,
      client_phone: client.telephone || '',
      client_email: client.email || '',
      client_address: client.adresse || ''
    });
    setClientSuggestions([]);
  };

  const addStockPart = (part) => {
    if (selectedStockParts.some((p) => p.reference === part.reference)) return;
    setSelectedStockParts((prev) => [...prev, part]);
  };

  const removeStockPart = (reference) => {
    setSelectedStockParts((prev) => prev.filter((p) => p.reference !== reference));
  };

  const stockPartsTotal = selectedStockParts.reduce(
    (sum, p) => sum + (p.unit_price || 0),
    0,
  );

  const addPart = (article) => {
    if (!formData.selected_parts.includes(article.reference)) {
      setFormData({
        ...formData,
        selected_parts: [...formData.selected_parts, article.reference]
      });
    }
    setStockSearch('');
    setShowStockResults(false);
  };

  const removePart = (ref) => {
    setFormData({
      ...formData,
      selected_parts: formData.selected_parts.filter(p => p !== ref)
    });
  };

  const togglePartCheck = (key) => {
    setFormData({
      ...formData,
      parts_check: {
        ...formData.parts_check,
        [key]: !formData.parts_check[key]
      }
    });
  };

  const toggleAllPartsDefective = () => {
    const allChecked = partsOptions.every(p => formData.parts_check[p.key]);
    const newPartsCheck = {};
    partsOptions.forEach(p => { newPartsCheck[p.key] = !allChecked; });
    setFormData({ ...formData, parts_check: newPartsCheck });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // C12.3 — magasin obligatoire à la création
    if (!isEditing && !magasinId) {
      alert(t('repairForm.selectMagasinFirst'));
      return;
    }
    setLoading(true);

    // Combine brand + model for storage
    const submitData = { ...formData };
    if (selectedStockParts.length > 0) {
      submitData.selected_parts = selectedStockParts.map((p) => p.reference);
    }
    if (brand && submitData.model && !submitData.model.toLowerCase().startsWith(brand.toLowerCase())) {
      submitData.model = `${brand} ${submitData.model}`;
    }
    if (!isEditing) submitData.magasin_id = magasinId;
    if (!isEditing && forceBackdate && forceDepotDate) {
      submitData.force_created_at = new Date(forceDepotDate).toISOString();
    }

    try {
      let deviceRef = ref;
      if (isEditing) {
        await api.put(`/devices/${ref}`, submitData);
        if (newPhotos.length > 0) {
          const fd = new FormData();
          newPhotos.forEach((f) => fd.append('files', f));
          await api.post(`/client/repair-request/${ref}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
        navigate(`/reparations/${ref}`);
        return;
      }
      const res = await api.post('/devices', submitData, {
          headers: { 'X-Magasin-Session-Id': magasinId },
        });
        deviceRef = res.data?.ref || res.data?.device?.ref;
      // Upload new photos
      if (newPhotos.length > 0 && deviceRef) {
        const fd = new FormData();
        newPhotos.forEach(f => fd.append('files', f));
        await api.post(`/client/repair-request/${deviceRef}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      // Finalize commande conversion (transfer acomptes)
      if (fromCommande && commandeId && deviceRef) {
        try {
          await api.post(`/commandes/${commandeId}/finalize-conversion`, { device_ref: deviceRef });
        } catch (err) {
          console.error('Error finalizing conversion:', err);
        }
      }
      // Phase H — show post-save QR label modal instead of navigating
      if (deviceRef) {
        setSavedRef(deviceRef);
        setShowQrModal(true);
      } else {
        navigate('/reparations');
      }
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('repairForm.saveError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto fade-in pb-24 px-1">
        <div className="rounded-xl border border-white/10 bg-[#151820] p-5 sm:p-6">
          <RepFormHeader
            isEditing={isEditing}
            refCode={ref}
            magasinId={isEditing ? formData.magasin_id : magasinId}
            onBack={() => navigate('/reparations')}
          />

          {fromCommande && prefillData && (
            <div className="mb-6 p-4 rounded-lg border border-amber-500/25 bg-amber-950/20">
              <div className="flex items-center gap-2 text-amber-300 text-sm font-semibold mb-1">
                <Wrench className="w-4 h-4" /> {t('repairForm.conversionFromCommande')}
              </div>
              <p className="text-xs text-gray-400">{t('repairForm.prefillNotice', { amount: formatCurrency(prefillData.total_acomptes || 0) })}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-2">
            {!isEditing && (
              <RepFormSection title={t('repairForm.sectionMagasin')}>
                <div data-testid="reparation-magasin-section">
                  <label className={labelClass}>{t('repairForm.magasinLabel')}</label>
                  <select
                    data-testid="reparation-magasin-select"
                    name="magasin_id"
                    value={magasinId}
                    onChange={(e) => setMagasinId(e.target.value.toUpperCase())}
                    className={inputClass}
                    required
                  >
                    <option value="">{t('repairForm.selectMagasinOption')}</option>
                    {magasinsList.map((m) => (
                      <option key={m.code} value={m.code}>{m.code} — {m.name || m.code}</option>
                    ))}
                  </select>
                </div>
              </RepFormSection>
            )}

            {!isEditing && isCarlos && (
              <RepFormSection title={t('repairForm.sectionForcedEntry')}>
                <p className="text-xs text-gray-500 mb-3">
                  {t('repairForm.forcedEntryHint')}
                </p>
                <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    checked={forceBackdate}
                    onChange={(e) => setForceBackdate(e.target.checked)}
                    className="rounded border-white/20"
                    data-testid="repair-force-backdate"
                  />
                  {t('repairForm.useBackdateLabel')}
                </label>
                {forceBackdate && (
                  <div>
                    <label className={labelClass}>{t('repairForm.realDepotDateTime')}</label>
                    <div className="relative">
                      <input
                        type="datetime-local"
                        value={forceDepotDate}
                        onChange={(e) => setForceDepotDate(e.target.value)}
                        className={`${inputClass} pl-10`}
                        required={forceBackdate}
                        max={new Date().toISOString().slice(0, 16)}
                        data-testid="repair-force-depot-date"
                      />
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    </div>
                  </div>
                )}
              </RepFormSection>
            )}

            <RepFormSection title={t('repairForm.sectionClient')}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative md:col-span-2">
                  <label className={labelClass}>{t('repairForm.clientName')}</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.client_name}
                      onChange={(e) => {
                        setFormData({ ...formData, client_name: e.target.value });
                        searchClients(e.target.value);
                      }}
                      className={`${inputClass} pl-10`}
                      required
                    />
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  </div>
                  {clientSuggestions.length > 0 && (
                    <div className="autocomplete-results">
                      {clientSuggestions.map((c) => (
                        <div key={c.id} onClick={() => selectClient(c)} className="autocomplete-item">
                          <strong>{c.nom}</strong> ({c.telephone})
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className={labelClass}>{t('repairForm.phone')}</label>
                  <div className="relative">
                    <input type="tel" value={formData.client_phone} onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })} className={`${inputClass} pl-10`} required />
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>{t('repairForm.email')}</label>
                  <div className="relative">
                    <input type="email" value={formData.client_email} onChange={(e) => setFormData({ ...formData, client_email: e.target.value })} className={`${inputClass} pl-10`} />
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className={labelClass}>{t('repairForm.address')}</label>
                  <div className="relative">
                    <input type="text" value={formData.client_address} onChange={(e) => setFormData({ ...formData, client_address: e.target.value })} className={`${inputClass} pl-10`} />
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  </div>
                </div>
              </div>
            </RepFormSection>

            <RepFormSection title={t('repairForm.sectionDevice')}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>{t('repairForm.brand')}</label>
                  <BrandSelector value={brand} onChange={(v) => { setBrand(v); setFormData({ ...formData, model: '' }); }} brands={allBrands} testId="repair-brand" />
                </div>
                <div>
                  <label className={labelClass}>{t('repairForm.model')}</label>
                  <ModelSelector value={formData.model} onChange={(v) => setFormData({ ...formData, model: v })} models={brandModelsMap[brand] || []} series={brandSeriesMap[brand] || {}} brand={brand} testId="repair-model" />
                </div>
                <div>
                  <label className={labelClass}>{t('repairForm.problem')}</label>
                  <div className="relative">
                    <input type="text" value={formData.problem} onChange={(e) => setFormData({ ...formData, problem: e.target.value })} className={`${inputClass} pl-10`} />
                    <AlertTriangle className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>{t('repairForm.technician')}</label>
                  <select value={formData.technician_name} onChange={(e) => setFormData({ ...formData, technician_name: e.target.value })} className={inputClass}>
                    <option value="">{t('repairForm.selectOption')}</option>
                    {technicians.map((tech) => <option key={tech} value={tech}>{tech}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>{t('repairForm.imeiSerial')}</label>
                  <div className="relative">
                    <input type="text" value={formData.imei_serial} onChange={(e) => setFormData({ ...formData, imei_serial: e.target.value })} className={`${inputClass} pl-10`} />
                    <Settings className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>{t('repairForm.deviceLock')}</label>
                  <div className="relative">
                    <input type="text" value={formData.device_lock} onChange={(e) => setFormData({ ...formData, device_lock: e.target.value })} className={`${inputClass} pl-10`} />
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  </div>
                </div>
              </div>
            </RepFormSection>

            <RepFormSection title={t('repairForm.sectionParts')}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-gray-500">{t('repairForm.defectivePartsHint')}</p>
                <button type="button" onClick={toggleAllPartsDefective} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-950/40 border border-red-500/25 text-red-300 text-xs font-semibold hover:bg-red-950/60" data-testid="phone-doesnt-turn-on-btn">
                  <PhoneOff className="w-3.5 h-3.5" /> {t('repairForm.phoneDoesntTurnOn')}
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {partsOptions.map(({ key }) => {
                  const on = formData.parts_check[key];
                  return (
                    <label key={key} className={`px-3 py-2 rounded-lg border cursor-pointer text-xs font-semibold transition-all ${on ? 'bg-red-950/40 border-red-500/40 text-red-300' : 'bg-black/20 border-white/10 text-gray-400 hover:border-white/25'}`}>
                      <input type="checkbox" checked={on || false} onChange={() => togglePartCheck(key)} className="hidden" />
                      {t(`repairForm.parts.${key}`)}
                    </label>
                  );
                })}
              </div>
            </RepFormSection>

            <RepFormSection title={t('repairForm.sectionStockParts')}>
              <p className="text-xs text-gray-500 mb-3">
                {t('repairForm.stockPartsHint')}
              </p>
              <RepairStockSearch
                api={api}
                magasinId={isEditing ? formData.magasin_id : magasinId}
                onSelect={addStockPart}
                excludeRefs={selectedStockParts.map((p) => p.reference)}
              />
              {selectedStockParts.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {selectedStockParts.map((part) => (
                    <span
                      key={part.reference}
                      className="px-3 py-1.5 rounded-lg bg-cyan-950/40 border border-cyan-500/30 text-cyan-300 text-xs font-medium flex items-center gap-2"
                    >
                      <Package className="w-3.5 h-3.5" />
                      {part.reference}
                      {part.unit_price > 0 && (
                        <span className="text-gray-400">({formatCurrency(part.unit_price)})</span>
                      )}
                      <button type="button" onClick={() => removeStockPart(part.reference)} className="hover:text-red-400">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {selectedStockParts.length > 0 && (
                <p className="text-xs text-emerald-400/90 mt-2">
                  {t('repairForm.stockPartsTotal', { amount: formatCurrency(stockPartsTotal) })}
                </p>
              )}
            </RepFormSection>

            <RepFormSection title={t('repairForm.sectionPricing')}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>
                    {t('repairForm.priceRepair')}
                    {selectedStockParts.length > 0 && (
                      <span className="text-amber-400/80 normal-case font-normal ml-1">{t('repairForm.theoreticalQuote')}</span>
                    )}
                  </label>
                  <div className="relative">
                    <input type="number" value={formData.price_repair} onChange={(e) => setFormData({ ...formData, price_repair: parseFloat(e.target.value) || 0 })} className={`${inputClass} pl-10`} step="0.01" min="0" />
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>{t('repairForm.priceDiagnostic')}</label>
                  <div className="relative">
                    <input type="number" value={formData.price_diagnostic} onChange={(e) => setFormData({ ...formData, price_diagnostic: parseFloat(e.target.value) || 0 })} className={`${inputClass} pl-10`} step="0.01" min="0" />
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <label className={labelClass}>{t('repairForm.receptionNotes')}</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className={`${inputClass} min-h-[88px] resize-y`} rows={3} />
              </div>
            </RepFormSection>

            <RepFormSection title={t('repairForm.sectionPhotos')}>
              <div className="flex flex-wrap gap-3">
                <label className="flex flex-col items-center justify-center gap-2 w-28 h-28 border-2 border-dashed border-white/15 rounded-xl cursor-pointer hover:border-red-500/40 transition-colors shrink-0">
                  <Upload className="w-5 h-5 text-gray-500" />
                  <span className="text-[10px] text-gray-500 text-center px-2">{t('repairForm.addPhoto')}</span>
                  <input type="file" multiple accept="image/*" onChange={(e) => setNewPhotos((prev) => [...prev, ...Array.from(e.target.files || [])])} className="hidden" data-testid="staff-repair-photos" />
                </label>
                {existingPhotos.map((url, i) => (
                  <a key={`ex-${i}`} href={`${process.env.REACT_APP_BACKEND_URL}${url}`} target="_blank" rel="noreferrer" className="w-28 h-28 rounded-xl overflow-hidden border border-white/10 shrink-0">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="w-full h-full object-cover" />
                  </a>
                ))}
                {newPhotos.map((f, i) => (
                  <div key={`new-${i}`} className="relative w-28 h-28 shrink-0 group">
                    <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover rounded-xl border border-white/10" />
                    <button type="button" onClick={() => setNewPhotos((prev) => prev.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </RepFormSection>
          </form>
        </div>

        <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-white/10 bg-[#0f1117]/95 backdrop-blur-sm px-4 py-3">
          <div className="max-w-3xl mx-auto flex gap-3">
            <button type="button" onClick={() => navigate('/reparations')} className="flex-1 min-h-[48px] rounded-lg bg-white/10 hover:bg-white/15 font-semibold text-gray-300">
              {t('repairForm.cancel')}
            </button>
            <button type="button" onClick={handleSubmit} disabled={loading} className="flex-1 min-h-[48px] rounded-lg bg-red-600 hover:bg-red-500 font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50">
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {isEditing ? t('repairForm.save') : t('repairForm.createRepair')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      <PostSaveRepairModal
        open={showQrModal}
        deviceRef={savedRef}
        defaultMode="ticket"
        onClose={() => { setShowQrModal(false); setSavedRef(null); navigate('/reparations'); }}
      />
    </Layout>
  );
};

export default ReparationFormPage;
